import { TrackerSession } from '../types'

const TIMEZONE = 'America/Bogota'
const WEEKLY_LIMIT_SECONDS = 42 * 60 * 60

const TRACKER_ERROR_TRANSLATIONS: Record<string, string> = {
  'This role cannot use office time tracking': 'Este rol no puede usar el registro de horas.',
  'This role cannot use admin hours metrics': 'Este rol no puede usar las métricas administrativas de horas.',
  'The current time is outside the office schedule': 'La hora actual está fuera del horario laboral permitido.',
  'Weekly limit already reached': 'Ya alcanzaste el límite semanal permitido.',
  'The workday is already closed': 'La jornada laboral ya fue cerrada.',
  'Only ejecutivo can register campaign location': 'Solo el rol ejecutivo puede registrar ubicación de campaña.',
  'locationType must be office or campaign': 'El tipo de ubicación debe ser oficina o campaña.',
  'capturedAt is required for executive geolocation':
    'La fecha de captura es obligatoria para la geolocalización del ejecutivo.',
  'latitude and longitude are required for executive geolocation':
    'La latitud y la longitud son obligatorias para la geolocalización del ejecutivo.',
  'permissionAttempts must be at least 1 when executive geolocation is sent':
    'Los intentos de permiso deben ser al menos 1 cuando se envía geolocalización del ejecutivo.',
  'No configured executive offices were found for validation':
    'No se encontraron oficinas configuradas para validar la ubicación del ejecutivo.',
  'Pause limit reached for this type': 'Ya alcanzaste el límite permitido para este tipo de pausa.',
  'Timer must be running to pause': 'El temporizador debe estar corriendo para poder pausarlo.',
  'Timer must be paused to resume': 'El temporizador debe estar en pausa para poder reanudarlo.',
  'Open pause was not found': 'No se encontró una pausa abierta para continuar la operación.',
  'pauseType must be break or almuerzo': 'El tipo de pausa debe ser break o almuerzo.',
  'mode must be day, week or month': 'El modo debe ser día, semana o mes.',
  'referenceDate is required for day mode': 'La fecha de referencia es obligatoria para el modo día.',
  'referenceDate is required for week mode': 'La fecha de referencia es obligatoria para el modo semana.',
  'referenceDate must use YYYY-MM-DD format': 'La fecha de referencia debe usar el formato YYYY-MM-DD.',
  'referenceMonth is required for month mode': 'El mes de referencia es obligatorio para el modo mes.',
  'referenceMonth must use YYYY-MM format': 'El mes de referencia debe usar el formato YYYY-MM.',
  'Invalid task secret': 'El secreto de la tarea es inválido.',
  'Only one temporal filter mode can be active': 'Solo puede haber un modo de filtro temporal activo.',
  'year must use YYYY format': 'El año debe usar el formato YYYY.',
  'month must use YYYY-MM format': 'El mes debe usar el formato YYYY-MM.',
  'startDate and endDate are required together': 'La fecha inicial y la fecha final deben enviarse juntas.',
  'startDate and endDate must use YYYY-MM-DD format':
    'La fecha inicial y la fecha final deben usar el formato YYYY-MM-DD.',
  'startDate cannot be after endDate': 'La fecha inicial no puede ser posterior a la fecha final.'
}

const TRACKER_ROLES = new Set(['ejecutivo', 'gestor', 'operaciones', 'gerente', 'agente'])

const normalizeRole = (role?: string | null) => {
  if (!role) {
    return null
  }

  return role === 'gestores' ? 'gestor' : role
}

const formatterCache = new Map<string, Intl.DateTimeFormat>()

const getFormatter = (key: string, options: Intl.DateTimeFormatOptions) => {
  if (!formatterCache.has(key)) {
    formatterCache.set(
      key,
      new Intl.DateTimeFormat('es-CO', {
        timeZone: TIMEZONE,
        ...options
      })
    )
  }

  return formatterCache.get(key)!
}

const getParts = (value: Date | string) => {
  const date = value instanceof Date ? value : new Date(value)
  const parts = getFormatter('parts', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).formatToParts(date)

  const map = parts.reduce<Record<string, string>>((accumulator, part) => {
    if (part.type !== 'literal') {
      accumulator[part.type] = part.value
    }

    return accumulator
  }, {})

  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    weekdayShort: map.weekday,
    hour: Number(map.hour),
    minute: Number(map.minute),
    second: Number(map.second)
  }
}

const createBogotaDate = (year: number, month: number, day: number, hour = 12, minute = 0, second = 0) =>
  new Date(
    `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(
      hour
    ).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}-05:00`
  )

const getCurrentLocalTime = () => {
  const parts = getParts(new Date())
  return createBogotaDate(parts.year, parts.month, parts.day, parts.hour, parts.minute, parts.second)
}

const formatLocalDateKey = (value: Date | string) => {
  const parts = getParts(value)
  return `${String(parts.year).padStart(4, '0')}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(
    2,
    '0'
  )}`
}

const formatSeconds = (seconds: number) => {
  const safeSeconds = Math.max(0, Math.floor(Number(seconds || 0)))
  const hours = String(Math.floor(safeSeconds / 3600)).padStart(2, '0')
  const minutes = String(Math.floor((safeSeconds % 3600) / 60)).padStart(2, '0')
  const remainingSeconds = String(safeSeconds % 60).padStart(2, '0')

  return `${hours}:${minutes}:${remainingSeconds}`
}

const getWeekDays = (weekStartDate?: string) => {
  const now = getCurrentLocalTime()
  const todayKey = formatLocalDateKey(now)
  const start = weekStartDate
    ? createBogotaDate(
        Number(weekStartDate.slice(0, 4)),
        Number(weekStartDate.slice(5, 7)),
        Number(weekStartDate.slice(8, 10))
      )
    : (() => {
        const localDay = now.getUTCDay()
        const mondayOffset = localDay === 0 ? -6 : 1 - localDay
        const startDate = new Date(now.getTime())
        startDate.setUTCDate(startDate.getUTCDate() + mondayOffset)
        return createBogotaDate(startDate.getUTCFullYear(), startDate.getUTCMonth() + 1, startDate.getUTCDate())
      })()

  return Array.from({ length: 7 }, (_, index) => {
    const current = new Date(start.getTime())
    current.setUTCDate(start.getUTCDate() + index)

    if (current.getUTCDay() === 0) {
      return null
    }

    return {
      key: formatLocalDateKey(current),
      label: getFormatter('label', {
        weekday: 'short',
        day: '2-digit'
      }).format(current),
      fullLabel: getFormatter('fullLabel', {
        weekday: 'long',
        day: '2-digit',
        month: 'short'
      }).format(current),
      isToday: formatLocalDateKey(current) === todayKey
    }
  }).filter((day): day is NonNullable<typeof day> => Boolean(day))
}

const getElapsedTodaySeconds = (session?: TrackerSession | null) => {
  if (!session) {
    return 0
  }

  if (session.status !== 'running' || !session.runningSince) {
    return Number(session.todayWorkedSeconds || 0)
  }

  const startedAt = new Date(session.runningSince).getTime()
  const elapsed = Math.floor(Math.max(0, Date.now() - startedAt) / 1000)
  return Number(session.todayWorkedSeconds || 0) + elapsed
}

const getRemainingWeekSeconds = (session?: TrackerSession | null) =>
  Math.max(0, WEEKLY_LIMIT_SECONDS - getElapsedWeekSeconds(session))

const getElapsedWeekSeconds = (session?: TrackerSession | null) => {
  if (!session) {
    return 0
  }

  if (session.status !== 'running' || !session.runningSince) {
    return Number(session.weekWorkedSeconds || 0)
  }

  const startedAt = new Date(session.runningSince).getTime()
  const elapsed = Math.floor(Math.max(0, Date.now() - startedAt) / 1000)
  return Number(session.weekWorkedSeconds || 0) + elapsed
}

const formatTimestamp = (value?: string | null) => {
  if (!value) {
    return '--'
  }

  return getFormatter('time', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).format(new Date(value))
}

const translateTrackerErrorMessage = (message?: string | null) => {
  if (!message) {
    return null
  }

  return TRACKER_ERROR_TRANSLATIONS[message] || message
}

const getErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === 'object' && error !== null) {
    const candidate = error as { response?: { data?: { message?: string } }; message?: string }
    return (
      translateTrackerErrorMessage(candidate.response?.data?.message) ||
      translateTrackerErrorMessage(candidate.message) ||
      fallback
    )
  }

  return fallback
}

export {
  TRACKER_ROLES,
  formatLocalDateKey,
  formatSeconds,
  formatTimestamp,
  getCurrentLocalTime,
  getElapsedTodaySeconds,
  getErrorMessage,
  getRemainingWeekSeconds,
  getWeekDays,
  normalizeRole
}
